import { v4 as uuid } from 'uuid';
import { forkJoin, mergeMap, of } from "rxjs";
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatStates from '@medsurf/flat-states';

@Injectable()
export class ChoiceEntityFacade {
  constructor(protected store: Store) {
  }

  //<editor-fold desc="Selectors">

  //</editor-fold>

  //<editor-fold desc="Requests">
  
  static getNewInstance(id: string,
                        question: FlatModels.QuestionEntityModels.QuestionEntityType,
                        order = 0,
                        label = '',
                        value: string | null = null) {
    const choice: FlatModels.ChoiceEntityModels.Choice = {
      id,
      label,
      value,
      order,
      question: question.id,
    };
    return choice;
  }
  
  static addChoice(store: Store,
                   id: string,
                   question: FlatModels.QuestionEntityModels.QuestionEntityType,
                   order = 0,
                   label = '',
                   value: string | null = null) {
    // Get New Instance
    const choice = ChoiceEntityFacade.getNewInstance(id, question, order, label, value);
    
    // Add Choice
    return store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(choice)).pipe(
      mergeMap((data) => of({data, choice})),
    );
  }
  
  static copyChoice(store: Store,
                    id: string,
                    question: FlatModels.QuestionEntityModels.QuestionEntityType,
                    clusterParentUpdate = false) {
    // Get Data
    const typedChoices = store.selectSnapshot(FlatStates.ChoiceEntityState.typedEntities$);
    
    // Get Choice
    const choice = structuredClone(typedChoices[id]);
    
    // Update Choice
    choice.id = uuid();
    choice.question = question.id;
    
    // Update Page
    question.choices.push(choice.id);
    
    // Copy Choice
    return of({}).pipe(
      mergeMap(() =>
        store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(choice))
      ),
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of(data);
        }
        return store.dispatch(new FlatActions.QuestionEntityActions.UpdateEntity(question));
      }),
    );
  }
  
  static copyChoices(store: Store,
                     ids: string[],
                     question: FlatModels.QuestionEntityModels.QuestionEntityType,
                     clusterParentUpdate = false) {
    if (ids.length === 0) {
      return of([]);
    }
    
    // Copy Annotation Targets
    return of({}).pipe(
      mergeMap(() =>
        forkJoin(ids.map(id => ChoiceEntityFacade.copyChoice(store, id, question, true)))
      ),
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of(data);
        }
        return store.dispatch(new FlatActions.QuestionEntityActions.UpdateEntity(question));
      }),
    );
  }

  static removeChoice(store: Store,
                      id: string,
                      clusterParentUpdate = false) {
    // Get Data
    const typedChoices = store.selectSnapshot(FlatStates.ChoiceEntityState.typedEntities$);
    const typedQuestions = store.selectSnapshot(FlatStates.QuestionEntityState.typedEntities$);

    // Get Choice
    const choice = structuredClone(typedChoices[id]);

    // Get Question
    const question = structuredClone(typedQuestions[choice.question]);
    question.choices.splice(question.choices.findIndex(id => id === choice.id), 1);

    // Remove Choice
    return store.dispatch(new FlatActions.BulkEditAuthorActions.RemoveChoice(id)).pipe(
      mergeMap(() =>
        store.dispatch(new FlatActions.ChoiceEntityActions.RemoveEntity(choice))
      ),
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of({data, question});
        }
        return store.dispatch(new FlatActions.QuestionEntityActions.UpdateEntity(question));
      }),
    );
  }

  static removeChoices(store: Store,
                       ids: string[]) {
    if (ids.length === 0) {
      return of([]);
    }

    // Remove Choices
    return of({}).pipe(
      mergeMap(() =>
        forkJoin(ids.map(id => ChoiceEntityFacade.removeChoice(store, id, true)))
      ),
      mergeMap((data: {data: Store, question: FlatModels.QuestionEntityModels.QuestionEntityType}[]) => {
        const questionMap = data.reduce((arr, clusterParentUpdate) => {
          const question = structuredClone(arr.get(clusterParentUpdate.question.id));
          if (question) {
            question.choices = clusterParentUpdate.question.choices.filter((id: string) => question.choices.includes(id));
            arr.set(clusterParentUpdate.question.id, question);
          } else {
            arr.set(clusterParentUpdate.question.id, clusterParentUpdate.question);
          }
          return arr;
        }, new Map<string, FlatModels.QuestionEntityModels.QuestionEntityType>());
        const questions = Array.from(questionMap.values());
        if (questions.length > 0) {
          return forkJoin(questions.map((question) => store.dispatch(new FlatActions.QuestionEntityActions.UpdateEntity(question))));
        }
        return of(data);
      }),
    );
  }

  //</editor-fold>
}
